import * as THREE from 'three';
import { cloneWithSharedGeometry } from '../../imported-model/loaders/part';
import { cloneGlbHierarchyUserDataMeta } from '../../imported-model/loaders/glb';
import {
  AssemblyReferenceCopyKind,
  AssemblyReferenceCopyLineageMetadataService,
  AssemblyReferenceCopyLineageRole,
  AssemblyReferenceCopyMetadataService,
  type AssemblyReferenceCopyLineageMetadata,
  type AssemblyReferenceCopyMetadata,
} from '../../assembly/reference-copy';
import { PuppyReferenceCopyTopologyRestorer } from './PuppyReferenceCopyTopologyRestorer';
import type { ImportedTopologySource } from '../../imported-model/loaders/types';
import { addObject3DToParent } from '../../utils/scene-object/addObject3DToParent';
import type {
  PuppyAssemblyDocument,
  PuppyAssemblyNode,
} from './types';

/**
 * 装配体引用复制 materialize 结果。
 */
export interface PuppyAssemblyReferenceCopyMaterializeResult {
  /** 成功生成的引用复制 occurrence 数量。 */
  readonly materializedCount: number;
}

/**
 * 引用复制导入期派生节点映射。
 */
interface ReferenceCopyNodeExpansion {
  /** 源节点 ID 到复制节点 ID 的映射。 */
  readonly copiedNodeIdBySourceNodeId: Map<string, string>;
}

//------ 装配体引用复制族 开始------
/**
 * 引用复制源节点解析输入。
 */
interface ReferenceCopySourceRootNodeResolveInput {
  /** 装配文档。 */
  readonly assembly: PuppyAssemblyDocument;
  /** 当前复制根节点。 */
  readonly copiedRootNode: PuppyAssemblyNode;
  /** 引用复制元数据。 */
  readonly referenceCopyMetadata: AssemblyReferenceCopyMetadata;
  /** 装配节点索引。 */
  readonly nodeById: ReadonlyMap<string, PuppyAssemblyNode>;
}
//------ 装配体引用复制族 结束------

/** 矩阵近似比较容差。 */
const REFERENCE_COPY_MATRIX_TOLERANCE = 1e-5;

/**
 * 判断未知值是否为普通记录。
 *
 * @param value 待判断值。
 * @returns 是否为普通记录。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

/**
 * 读取对象上的 PuppyCAD display 用户数据。
 *
 * @param object 场景对象。
 * @returns puppy 用户数据。
 */
function readPuppyUserData(object: THREE.Object3D): Record<string, unknown> | undefined {
  return isRecord(object.userData?.puppy) ? object.userData.puppy as Record<string, unknown> : undefined;
}

/**
 * 读取 display 节点对应的 assembly node ID。
 *
 * @param object 场景对象。
 * @returns assembly node ID。
 */
function readAssemblyNodeId(object: THREE.Object3D): string {
  const value = readPuppyUserData(object)?.assemblyNodeId;
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * 收集当前 display.glb 运行时对象索引。
 *
 * @param root display 根对象。
 * @returns assembly node ID 到 Object3D。
 */
function collectObjectByAssemblyNodeId(root: THREE.Object3D): Map<string, THREE.Object3D> {
  const objectByNodeId = new Map<string, THREE.Object3D>();
  root.traverse((object) => {
    const nodeId = readAssemblyNodeId(object);
    if (nodeId && !objectByNodeId.has(nodeId)) {
      objectByNodeId.set(nodeId, object);
    }
  });
  return objectByNodeId;
}

/**
 * 判断对象是否承载可渲染几何。
 *
 * @param object 待判断对象。
 * @returns 是否为运行时可见几何对象。
 */
function isRenderableObject(object: THREE.Object3D): boolean {
  return object instanceof THREE.Mesh
    || object instanceof THREE.Line
    || object instanceof THREE.Points
    || object instanceof THREE.Sprite;
}

/**
 * 判断对象子树是否存在可渲染 payload。
 *
 * @param root 子树根对象。
 * @returns 是否存在可渲染几何。
 */
function hasRenderablePayload(root: THREE.Object3D): boolean {
  let hasRenderable = false;
  root.traverse((object) => {
    if (hasRenderable || !isRenderableObject(object)) {
      return;
    }
    hasRenderable = true;
  });
  return hasRenderable;
}

/**
 * 判断祖先关系。
 *
 * @param ancestor 候选祖先对象。
 * @param object 候选后代对象。
 * @returns object 是否位于 ancestor 子树内。
 */
function isAncestorOf(ancestor: THREE.Object3D, object: THREE.Object3D): boolean {
  let current = object.parent;
  while (current) {
    if (current === ancestor) {
      return true;
    }
    current = current.parent;
  }
  return false;
}

/**
 * 比较两个矩阵是否近似相等。
 *
 * @param left 左侧矩阵。
 * @param right 右侧矩阵。
 * @returns 是否在容差范围内相等。
 */
function areMatricesClose(left: THREE.Matrix4, right: THREE.Matrix4): boolean {
  return left.elements.every((value, index) => (
    Math.abs(value - right.elements[index]!) <= REFERENCE_COPY_MATRIX_TOLERANCE
  ));
}

/**
 * 将装配节点矩阵读取为 THREE.Matrix4。
 *
 * @param node 装配节点。
 * @returns 节点局部矩阵。
 */
function createMatrixFromAssemblyNode(node: PuppyAssemblyNode): THREE.Matrix4 {
  if (!node.transform) {
    throw new Error(`PuppyCAD 引用复制装配节点缺少本地矩阵: ${node.nodeId}`);
  }
  return new THREE.Matrix4().fromArray(node.transform);
}

/**
 * 将矩阵左乘到对象局部矩阵。
 *
 * @param object 目标对象。
 * @param matrix 左乘矩阵。
 */
function premultiplyLocalMatrix(object: THREE.Object3D, matrix: THREE.Matrix4): void {
  object.updateMatrix();
  const nextMatrix = new THREE.Matrix4().multiplyMatrices(matrix, object.matrix);
  nextMatrix.decompose(object.position, object.quaternion, object.scale);
  object.updateMatrix();
  object.updateMatrixWorld(true);
}

/**
 * 从 assembly.json 节点扩展中读取引用复制元数据。
 *
 * @param node 装配节点。
 * @returns 引用复制元数据；普通节点返回 null。
 */
function readReferenceCopyMetadata(node: PuppyAssemblyNode): AssemblyReferenceCopyMetadata | null {
  const value = node.extensions?.referenceCopy;
  if (!isRecord(value) || value.kind !== AssemblyReferenceCopyKind.ReferenceOccurrence) {
    return null;
  }

  const sourceNodeId = typeof value.sourceNodeId === 'string' ? value.sourceNodeId.trim() : '';
  if (!sourceNodeId) {
    return null;
  }

  const sourcePartFileUuid = typeof value.sourcePartFileUuid === 'string'
    ? value.sourcePartFileUuid.trim()
    : '';
  const sourceComponentInstanceId = typeof value.sourceComponentInstanceId === 'string'
    ? value.sourceComponentInstanceId.trim()
    : '';
  const parentNodeId = typeof value.parentNodeId === 'string' ? value.parentNodeId.trim() : '';
  //------ 装配体引用复制族 开始------
  const referenceFamilyId = typeof value.referenceFamilyId === 'string' ? value.referenceFamilyId.trim() : '';
  const definitionId = typeof value.definitionId === 'string' ? value.definitionId.trim() : '';
  const originOccurrenceId = typeof value.originOccurrenceId === 'string' ? value.originOccurrenceId.trim() : '';
  const copiedFromOccurrenceId = typeof value.copiedFromOccurrenceId === 'string'
    ? value.copiedFromOccurrenceId.trim()
    : '';
  //------ 装配体引用复制族 结束------
  return {
    kind: AssemblyReferenceCopyKind.ReferenceOccurrence,
    sourceNodeId,
    ...(sourcePartFileUuid ? { sourcePartFileUuid } : {}),
    ...(sourceComponentInstanceId ? { sourceComponentInstanceId } : {}),
    ...(parentNodeId ? { parentNodeId } : {}),
    //------ 装配体引用复制族 开始------
    ...(referenceFamilyId ? { referenceFamilyId } : {}),
    ...(definitionId ? { definitionId } : {}),
    ...(originOccurrenceId ? { originOccurrenceId } : {}),
    ...(copiedFromOccurrenceId ? { copiedFromOccurrenceId } : {}),
    //------ 装配体引用复制族 结束------
  };
}

//------ 装配体引用复制族 开始------
/**
 * 读取引用复制族角色。
 *
 * @param value 待读取值。
 * @returns 有效角色；无效时返回 null。
 */
function readReferenceLineageRole(value: unknown): AssemblyReferenceCopyLineageRole | null {
  return value === AssemblyReferenceCopyLineageRole.OriginOccurrence
    || value === AssemblyReferenceCopyLineageRole.ReferenceOccurrence
      ? value
      : null;
}

/**
 * 从 assembly.json 节点扩展中读取引用复制族元数据。
 *
 * @param node 装配节点。
 * @returns 引用复制族元数据；普通节点返回 null。
 */
function readReferenceLineageMetadata(node: PuppyAssemblyNode): AssemblyReferenceCopyLineageMetadata | null {
  const value = node.extensions?.referenceLineage;
  if (!isRecord(value)) {
    return null;
  }

  const referenceFamilyId = typeof value.referenceFamilyId === 'string' ? value.referenceFamilyId.trim() : '';
  const definitionId = typeof value.definitionId === 'string' ? value.definitionId.trim() : '';
  const originOccurrenceId = typeof value.originOccurrenceId === 'string' ? value.originOccurrenceId.trim() : '';
  const role = readReferenceLineageRole(value.role);
  if (!referenceFamilyId || !definitionId || !originOccurrenceId || !role) {
    return null;
  }

  const copiedFromOccurrenceId = typeof value.copiedFromOccurrenceId === 'string'
    ? value.copiedFromOccurrenceId.trim()
    : '';
  return {
    referenceFamilyId,
    definitionId,
    originOccurrenceId,
    role,
    ...(copiedFromOccurrenceId ? { copiedFromOccurrenceId } : {}),
  };
}
//------ 装配体引用复制族 结束------

/**
 * 创建装配节点索引。
 *
 * @param assembly 装配结构文档。
 * @returns 节点 ID 到节点的映射。
 */
function createNodeById(assembly: PuppyAssemblyDocument): Map<string, PuppyAssemblyNode> {
  return new Map(assembly.nodes.map((node) => [node.nodeId, node]));
}

/**
 * 创建派生复制节点 ID。
 *
 * @param copiedRootNodeId 复制根节点 ID。
 * @param sourceNodeId 源节点 ID。
 * @returns 派生复制节点 ID。
 */
function createDerivedCopiedNodeId(copiedRootNodeId: string, sourceNodeId: string): string {
  return `${copiedRootNodeId}__ref__${sourceNodeId}`;
}

/**
 * 创建不冲突的派生复制节点 ID。
 *
 * @param baseNodeId 基础节点 ID。
 * @param nodeById 现有节点索引。
 * @returns 唯一节点 ID。
 */
function createUniqueDerivedCopiedNodeId(
  baseNodeId: string,
  nodeById: ReadonlyMap<string, PuppyAssemblyNode>,
): string {
  if (!nodeById.has(baseNodeId)) {
    return baseNodeId;
  }

  let index = 1;
  let candidateNodeId = `${baseNodeId}__${index}`;
  while (nodeById.has(candidateNodeId)) {
    index += 1;
    candidateNodeId = `${baseNodeId}__${index}`;
  }
  return candidateNodeId;
}

/**
 * 解析派生节点在复制根下的路径。
 *
 * @param sourceRootNode 源根节点。
 * @param copiedRootNode 复制根节点。
 * @param sourceNode 源子节点。
 * @returns 派生节点路径。
 */
function resolveDerivedCopiedNodePath(
  sourceRootNode: PuppyAssemblyNode,
  copiedRootNode: PuppyAssemblyNode,
  sourceNode: PuppyAssemblyNode,
): string {
  const sourceRootPath = sourceRootNode.path.trim();
  const sourceNodePath = sourceNode.path.trim();
  const relativePath = sourceRootPath && sourceNodePath.startsWith(sourceRootPath)
    ? sourceNodePath.slice(sourceRootPath.length).replace(/^\/+/, '')
    : sourceNode.name;

  return relativePath ? `${copiedRootNode.path}/${relativePath}` : `${copiedRootNode.path}/${sourceNode.name}`;
}

/**
 * 克隆导入期派生节点。
 *
 * @param input 克隆输入。
 * @returns 派生节点。
 */
function cloneReferenceCopyDerivedNode(input: {
  sourceRootNode: PuppyAssemblyNode;
  copiedRootNode: PuppyAssemblyNode;
  sourceNode: PuppyAssemblyNode;
  copiedNodeId: string;
  copiedParentNodeId: string;
}): PuppyAssemblyNode {
  const { referenceCopy: _ignoredReferenceCopy, ...safeExtensions } = input.sourceNode.extensions ?? {};
  void _ignoredReferenceCopy;

  return {
    ...input.sourceNode,
    nodeId: input.copiedNodeId,
    parentNodeId: input.copiedParentNodeId,
    path: resolveDerivedCopiedNodePath(input.sourceRootNode, input.copiedRootNode, input.sourceNode),
    meta: input.sourceNode.meta ? cloneGlbHierarchyUserDataMeta(input.sourceNode.meta) : undefined,
    displayNodeIds: [...input.sourceNode.displayNodeIds],
    bodyBindingIds: [...input.sourceNode.bodyBindingIds],
    geometryObjectIds: [...input.sourceNode.geometryObjectIds],
    extensions: Object.keys(safeExtensions).length > 0 ? safeExtensions : undefined,
  };
}

/**
 * 判断源节点是否属于可派生的定义子树。
 *
 * 设计说明：
 * 引用复制节点是 occurrence，不是源装配定义的一部分。复制 assembly 根时若继续派生这些节点，
 * 会把复制体再次复制到自己名下，导致重载后出现重复 transform root 与重复可见几何。
 *
 * @param input 源子节点与当前复制根。
 * @returns 可作为源定义子节点派生时返回 true。
 */
function canDeriveReferenceCopySourceChild(input: {
  sourceChildNode: PuppyAssemblyNode;
  copiedRootNode: PuppyAssemblyNode;
}): boolean {
  if (input.sourceChildNode.nodeId === input.copiedRootNode.nodeId) {
    return false;
  }
  if (readReferenceCopyMetadata(input.sourceChildNode)) {
    return false;
  }

  return readReferenceLineageMetadata(input.sourceChildNode)?.role !== AssemblyReferenceCopyLineageRole.ReferenceOccurrence;
}

//------ 装配体引用复制族 开始------
/**
 * 判断装配节点是否属于指定引用复制族。
 *
 * @param node 装配节点。
 * @param referenceFamilyId 引用复制族 ID。
 * @returns 是否属于同一复制族。
 */
function isReferenceCopyFamilyNode(node: PuppyAssemblyNode, referenceFamilyId: string): boolean {
  const lineageMetadata = readReferenceLineageMetadata(node);
  const referenceCopyMetadata = readReferenceCopyMetadata(node);
  const nodeReferenceFamilyId = lineageMetadata?.referenceFamilyId
    ?? referenceCopyMetadata?.referenceFamilyId
    ?? '';
  return nodeReferenceFamilyId === referenceFamilyId;
}

/**
 * 解析引用复制导入展开时使用的源根节点。
 *
 * 设计说明：
 * 原始 occurrence 被删除后，`.puppy` 中可能只剩同族复制体。此时允许用同族第一个
 * 代表节点展开子树，但必须排除当前复制根节点自身，否则会把待展开节点当成源节点，
 * 导致下游草图、点、线、面节点无法还原。
 *
 * @param input 解析输入。
 * @returns 源根节点；无法解析时返回 null。
 */
function resolveReferenceCopySourceRootNode(
  input: ReferenceCopySourceRootNodeResolveInput,
): PuppyAssemblyNode | null {
  const directSourceNode = input.nodeById.get(input.referenceCopyMetadata.sourceNodeId);
  if (directSourceNode && directSourceNode.nodeId !== input.copiedRootNode.nodeId) {
    return directSourceNode;
  }

  const copiedFromNode = input.referenceCopyMetadata.copiedFromOccurrenceId
    ? input.nodeById.get(input.referenceCopyMetadata.copiedFromOccurrenceId)
    : undefined;
  if (copiedFromNode && copiedFromNode.nodeId !== input.copiedRootNode.nodeId) {
    return copiedFromNode;
  }

  const referenceFamilyId = input.referenceCopyMetadata.referenceFamilyId?.trim() ?? '';
  if (!referenceFamilyId) {
    return null;
  }

  const familySourceNode = input.assembly.nodes.find((node) => (
    node.nodeId !== input.copiedRootNode.nodeId
    && isReferenceCopyFamilyNode(node, referenceFamilyId)
  ));

  return familySourceNode ?? null;
}
//------ 装配体引用复制族 结束------

/**
 * 根据源节点子树在导入期展开引用复制的子节点。
 *
 * 设计说明：
 * 文件中只保存复制根节点的来源，位置由复制节点自身 transform 承载；这里根据源节点树派生子节点，
 * 保证重载后的设计树仍能展示复制体下游子结构，同时不增加 `.puppy` 持久化体积。
 *
 * @param input 展开输入。
 * @returns 源节点与派生复制节点的映射。
 */
function expandReferenceCopyDerivedNodes(input: {
  assembly: PuppyAssemblyDocument;
  copiedRootNode: PuppyAssemblyNode;
  referenceCopyMetadata: AssemblyReferenceCopyMetadata;
  nodeById: Map<string, PuppyAssemblyNode>;
}): ReferenceCopyNodeExpansion {
  const sourceRootNode = resolveReferenceCopySourceRootNode({
    assembly: input.assembly,
    copiedRootNode: input.copiedRootNode,
    referenceCopyMetadata: input.referenceCopyMetadata,
    nodeById: input.nodeById,
  });
  if (!sourceRootNode) {
    throw new Error(`PuppyCAD 引用复制缺少源装配节点: ${input.referenceCopyMetadata.sourceNodeId}`);
  }

  const copiedNodeIdBySourceNodeId = new Map<string, string>([
    [sourceRootNode.nodeId, input.copiedRootNode.nodeId],
  ]);

  const appendChildren = (sourceParentNodeId: string, copiedParentNodeId: string): void => {
    const sourceChildIds = input.assembly.childIdsByNodeId[sourceParentNodeId] ?? [];
    const copiedChildIds = [...(input.assembly.childIdsByNodeId[copiedParentNodeId] ?? [])];

    sourceChildIds.forEach((sourceChildNodeId) => {
      const sourceChildNode = input.nodeById.get(sourceChildNodeId);
      if (!sourceChildNode) {
        return;
      }
      if (!canDeriveReferenceCopySourceChild({
        sourceChildNode,
        copiedRootNode: input.copiedRootNode,
      })) {
        return;
      }

      const copiedChildNodeId = createUniqueDerivedCopiedNodeId(
        createDerivedCopiedNodeId(input.copiedRootNode.nodeId, sourceChildNode.nodeId),
        input.nodeById,
      );
      const copiedChildNode = cloneReferenceCopyDerivedNode({
        sourceRootNode,
        copiedRootNode: input.copiedRootNode,
        sourceNode: sourceChildNode,
        copiedNodeId: copiedChildNodeId,
        copiedParentNodeId: copiedParentNodeId,
      });

      input.assembly.nodes.push(copiedChildNode);
      input.assembly.childIdsByNodeId[copiedChildNodeId] = [];
      input.nodeById.set(copiedChildNodeId, copiedChildNode);
      copiedChildIds.push(copiedChildNodeId);
      copiedNodeIdBySourceNodeId.set(sourceChildNode.nodeId, copiedChildNodeId);

      appendChildren(sourceChildNode.nodeId, copiedChildNodeId);
    });

    input.assembly.childIdsByNodeId[copiedParentNodeId] = copiedChildIds;
  };

  appendChildren(sourceRootNode.nodeId, input.copiedRootNode.nodeId);

  return {
    copiedNodeIdBySourceNodeId,
  };
}

/**
 * 将克隆对象子树改写为新的 assembly node 归属。
 *
 * @param root 克隆对象根。
 * @param expansion 引用复制节点展开映射。
 */
function rewritePuppyAssemblyNodeId(
  root: THREE.Object3D,
  expansion: ReferenceCopyNodeExpansion,
): void {
  const copiedRootNodeId = expansion.copiedNodeIdBySourceNodeId.values().next().value ?? '';
  if (!copiedRootNodeId) {
    return;
  }

  root.traverse((object) => {
    const sourceNodeId = readAssemblyNodeId(object);
    const copiedNodeId = sourceNodeId
      ? expansion.copiedNodeIdBySourceNodeId.get(sourceNodeId) ?? copiedRootNodeId
      : copiedRootNodeId;

    const puppy = isRecord(object.userData?.puppy) ? object.userData.puppy as Record<string, unknown> : {};
    object.userData = {
      ...object.userData,
      puppy: {
        ...puppy,
        assemblyNodeId: copiedNodeId,
      },
    };
  });
}

/**
 * 将克隆对象按新的 assembly node ID 写回运行时索引。
 *
 * @param root 克隆对象根。
 * @param objectByNodeId 运行时对象索引。
 */
function indexMaterializedObjects(root: THREE.Object3D, objectByNodeId: Map<string, THREE.Object3D>): void {
  root.traverse((object) => {
    const nodeId = readAssemblyNodeId(object);
    if (nodeId && !objectByNodeId.has(nodeId)) {
      objectByNodeId.set(nodeId, object);
    }
  });
}

//------ 装配体引用复制族 开始------
/**
 * 引用复制 materialize 源对象索引。
 */
interface ReferenceCopyMaterializeSourceIndex {
  /** 引用复制族 ID 到代表 Object3D 的索引。 */
  readonly objectByReferenceFamilyId: Map<string, THREE.Object3D>;
  /** 稳定定义 ID 到代表 Object3D 的索引。 */
  readonly objectByDefinitionId: Map<string, THREE.Object3D>;
}

/**
 * 将引用复制或复制族元数据绑定到已有 display 对象。
 *
 * @param object 目标 Object3D。
 * @param referenceCopyMetadata 引用复制元数据。
 * @param lineageMetadata 引用复制族元数据。
 */
function writeReferenceCopyRuntimeMetadata(
  object: THREE.Object3D,
  referenceCopyMetadata: AssemblyReferenceCopyMetadata | null,
  lineageMetadata: AssemblyReferenceCopyLineageMetadata | null,
): void {
  if (lineageMetadata) {
    AssemblyReferenceCopyLineageMetadataService.writeMetadata(object, lineageMetadata);
  }
  if (referenceCopyMetadata) {
    AssemblyReferenceCopyMetadataService.writeMetadata(object, referenceCopyMetadata);
  }
}

/**
 * 创建引用复制 materialize 源对象索引。
 *
 * @param assembly 装配文档。
 * @param objectByNodeId 当前 display 运行时对象索引。
 * @returns 源对象索引。
 */
function createReferenceCopyMaterializeSourceIndex(
  assembly: PuppyAssemblyDocument,
  objectByNodeId: ReadonlyMap<string, THREE.Object3D>,
): ReferenceCopyMaterializeSourceIndex {
  const objectByReferenceFamilyId = new Map<string, THREE.Object3D>();
  const objectByDefinitionId = new Map<string, THREE.Object3D>();

  assembly.nodes.forEach((node) => {
    const object = objectByNodeId.get(node.nodeId);
    if (!object || !hasRenderablePayload(object)) {
      return;
    }

    const lineageMetadata = readReferenceLineageMetadata(node);
    const referenceCopyMetadata = readReferenceCopyMetadata(node);
    const referenceFamilyId = lineageMetadata?.referenceFamilyId ?? referenceCopyMetadata?.referenceFamilyId ?? '';
    const definitionId = lineageMetadata?.definitionId
      ?? referenceCopyMetadata?.definitionId
      ?? referenceCopyMetadata?.sourcePartFileUuid
      ?? '';
    if (referenceFamilyId && !objectByReferenceFamilyId.has(referenceFamilyId)) {
      objectByReferenceFamilyId.set(referenceFamilyId, object);
    }
    if (definitionId && !objectByDefinitionId.has(definitionId)) {
      objectByDefinitionId.set(definitionId, object);
    }
  });

  return {
    objectByReferenceFamilyId,
    objectByDefinitionId,
  };
}

/**
 * 将已具备可渲染 payload 的复制族节点写入本次导入的源对象索引。
 *
 * @param node 装配节点。
 * @param object 运行时对象。
 * @param sourceIndex 源对象索引。
 */
function indexReferenceCopyMaterializeSourceNode(
  node: PuppyAssemblyNode,
  object: THREE.Object3D,
  sourceIndex: ReferenceCopyMaterializeSourceIndex,
): void {
  if (!hasRenderablePayload(object)) {
    return;
  }

  const lineageMetadata = readReferenceLineageMetadata(node);
  const referenceCopyMetadata = readReferenceCopyMetadata(node);
  const referenceFamilyId = lineageMetadata?.referenceFamilyId ?? referenceCopyMetadata?.referenceFamilyId ?? '';
  const definitionId = lineageMetadata?.definitionId
    ?? referenceCopyMetadata?.definitionId
    ?? referenceCopyMetadata?.sourcePartFileUuid
    ?? '';
  if (referenceFamilyId && !sourceIndex.objectByReferenceFamilyId.has(referenceFamilyId)) {
    sourceIndex.objectByReferenceFamilyId.set(referenceFamilyId, object);
  }
  if (definitionId && !sourceIndex.objectByDefinitionId.has(definitionId)) {
    sourceIndex.objectByDefinitionId.set(definitionId, object);
  }
}

//------ 装配体引用复制 payload 回填 开始------
/**
 * 向字符串数组追加去重值。
 *
 * @param target 目标数组。
 * @param values 候选值。
 */
function appendUniqueStrings(target: string[], values: readonly string[]): void {
  values.forEach((value) => {
    if (!target.includes(value)) {
      target.push(value);
    }
  });
}

/**
 * 将复制节点误带的 display/body/geometry 绑定补回源节点。
 *
 * @param input 绑定迁移输入。
 */
function moveMisplacedReferenceCopyPayloadBindings(input: {
  sourceNode: PuppyAssemblyNode;
  copiedNode: PuppyAssemblyNode;
}): void {
  appendUniqueStrings(input.sourceNode.displayNodeIds, input.copiedNode.displayNodeIds);
  appendUniqueStrings(input.sourceNode.bodyBindingIds, input.copiedNode.bodyBindingIds);
  appendUniqueStrings(input.sourceNode.geometryObjectIds, input.copiedNode.geometryObjectIds);
  input.copiedNode.displayNodeIds = [];
  input.copiedNode.bodyBindingIds = [];
  input.copiedNode.geometryObjectIds = [];
}

/**
 * 将对象子树的装配节点归属改写为指定节点。
 *
 * @param root 子树根。
 * @param assemblyNodeId 目标装配节点 ID。
 */
function rewritePuppyAssemblyNodeIdToSingleNode(root: THREE.Object3D, assemblyNodeId: string): void {
  root.traverse((object) => {
    const puppy = isRecord(object.userData?.puppy) ? object.userData.puppy as Record<string, unknown> : {};
    object.userData = {
      ...object.userData,
      puppy: {
        ...puppy,
        assemblyNodeId,
      },
    };
  });
}

/**
 * 收集子树中的顶层可渲染 payload 根。
 *
 * @param root 子树根。
 * @returns 顶层 payload 根对象。
 */
function collectTopLevelRenderablePayloadRoots(root: THREE.Object3D): THREE.Object3D[] {
  if (isRenderableObject(root)) {
    return [root];
  }

  const payloadRoots: THREE.Object3D[] = [];
  root.children.forEach((child) => {
    if (!hasRenderablePayload(child)) {
      return;
    }
    if (payloadRoots.some((payloadRoot) => isAncestorOf(payloadRoot, child))) {
      return;
    }
    payloadRoots.push(child);
  });
  return payloadRoots;
}

/**
 * 克隆代表 payload 到源节点 host。
 *
 * @param input 克隆输入。
 * @returns 克隆出的 payload 根对象。
 */
function cloneRepresentativePayloadToSourceHost(input: {
  representativeObject: THREE.Object3D;
  sourceObject: THREE.Object3D;
  sourceNode: PuppyAssemblyNode;
}): THREE.Object3D[] {
  const payloadRoots = collectTopLevelRenderablePayloadRoots(input.representativeObject);
  const clonedPayloadRoots = payloadRoots.map((payloadRoot) => {
    const clonedPayload = cloneWithSharedGeometry(payloadRoot);
    if (payloadRoot === input.representativeObject) {
      /**
       * 当代表体本身就是 mesh 时，它的局部矩阵是 occurrence 矩阵。
       * 源节点 host 已经保存了自己的 occurrence 矩阵，因此 payload 挂回源节点前必须归零。
       */
      clonedPayload.position.set(0, 0, 0);
      clonedPayload.quaternion.identity();
      clonedPayload.scale.set(1, 1, 1);
      clonedPayload.updateMatrix();
    }
    AssemblyReferenceCopyMetadataService.clearMetadataInTree(clonedPayload);
    AssemblyReferenceCopyLineageMetadataService.clearMetadataInTree(clonedPayload);
    rewritePuppyAssemblyNodeIdToSingleNode(clonedPayload, input.sourceNode.nodeId);
    addObject3DToParent(clonedPayload, input.sourceObject);
    return clonedPayload;
  });

  if (clonedPayloadRoots.length > 0) {
    input.sourceObject.updateMatrixWorld(true);
  }
  return clonedPayloadRoots;
}

/**
 * 重置被挂到 occurrence host 下的 payload 根局部变换。
 *
 * @param object 需要重置局部变换的 payload 根。
 */
function resetHostedPayloadLocalTransform(object: THREE.Object3D): void {
  object.position.set(0, 0, 0);
  object.quaternion.identity();
  object.scale.set(1, 1, 1);
  object.updateMatrix();
}

/**
 * 克隆引用复制需要的顶层可渲染 payload。
 *
 * 设计说明：
 * 装配复制根可能位于源装配根之下，直接克隆源根会把已存在的复制 host 也带入新 payload。
 * 因此 materialize 的克隆边界必须停在源根下的顶层可渲染对象，而不是源装配 Object3D 自身。
 *
 * @param input 源对象与引用复制展开映射。
 * @returns 已改写 assemblyNodeId 的 payload 克隆根。
 */
function cloneReferenceCopyPayloadRoots(input: {
  sourceObject: THREE.Object3D;
  expansion: ReferenceCopyNodeExpansion;
}): THREE.Object3D[] {
  return collectTopLevelRenderablePayloadRoots(input.sourceObject).map((payloadRoot) => {
    const clonedPayloadRoot = cloneWithSharedGeometry(payloadRoot);
    if (payloadRoot === input.sourceObject) {
      /**
       * 源对象本身是 mesh 时，它的局部矩阵属于源 occurrence；复制 host 会承载新的
       * occurrence 矩阵，因此 payload 挂入前必须归零。
       */
      resetHostedPayloadLocalTransform(clonedPayloadRoot);
    }
    AssemblyReferenceCopyMetadataService.clearMetadataInTree(clonedPayloadRoot);
    AssemblyReferenceCopyLineageMetadataService.clearMetadataInTree(clonedPayloadRoot);
    rewritePuppyAssemblyNodeId(clonedPayloadRoot, input.expansion);
    return clonedPayloadRoot;
  });
}

/**
 * 把引用复制 payload 挂入已有空 host。
 *
 * @param input 已有 host 与待挂入 payload。
 */
function attachReferenceCopyPayloadRootsToExistingHost(input: {
  existingObject: THREE.Object3D;
  payloadRoots: readonly THREE.Object3D[];
}): void {
  input.payloadRoots.forEach((payloadRoot) => {
    addObject3DToParent(payloadRoot, input.existingObject);
  });
  input.existingObject.updateMatrixWorld(true);
}

/**
 * 创建引用复制运行时 host，并挂载克隆 payload。
 *
 * @param input 复制节点、运行时 metadata 与 payload。
 * @returns 可挂入场景树的复制 host。
 */
function createReferenceCopyRuntimeHost(input: {
  node: PuppyAssemblyNode;
  referenceCopyMetadata: AssemblyReferenceCopyMetadata;
  lineageMetadata: AssemblyReferenceCopyLineageMetadata | null;
  payloadRoots: readonly THREE.Object3D[];
}): THREE.Object3D {
  const host = new THREE.Group();
  host.name = input.node.name;
  host.userData = {
    ...host.userData,
    puppy: {
      assemblyNodeId: input.node.nodeId,
      assemblyNodeRole: 'node',
    },
  };
  writeReferenceCopyRuntimeMetadata(host, input.referenceCopyMetadata, input.lineageMetadata);
  input.payloadRoots.forEach((payloadRoot) => {
    addObject3DToParent(payloadRoot, host);
  });
  applyReferenceCopyTransform(host, input.node);
  return host;
}

/**
 * 从复制 host 上移除已经误持久化的可渲染 payload。
 *
 * @param copiedObject 复制节点 host。
 * @returns 是否移除了 payload。
 */
function detachRenderablePayloadFromCopiedHost(copiedObject: THREE.Object3D): boolean {
  const payloadRoots = collectTopLevelRenderablePayloadRoots(copiedObject)
    .filter((payloadRoot) => payloadRoot !== copiedObject);
  payloadRoots.forEach((payloadRoot) => {
    payloadRoot.removeFromParent();
  });
  if (payloadRoots.length > 0) {
    copiedObject.updateMatrixWorld(true);
  }
  return payloadRoots.length > 0;
}

/**
 * 修复旧包中复制节点误承担代表 payload 的情况。
 *
 * 设计说明：
 * 目标格式要求源 occurrence 承担 display/BRep/topology payload，复制 occurrence 只保存
 * `referenceCopy` 引用信息。部分历史包中源节点仍存在但为空，复制节点却带着唯一 payload。
 * 这里先把 payload 克隆回源节点，再清空复制 host，让后续 materialize 继续从源节点克隆复制体。
 *
 * @param input 修复输入。
 * @returns 是否已经清空复制 host，后续需要继续 materialize。
 */
function repairMisplacedReferenceCopyRepresentativePayload(input: {
  copiedObject: THREE.Object3D;
  copiedNode: PuppyAssemblyNode;
  sourceNode: PuppyAssemblyNode | null;
  objectByNodeId: Map<string, THREE.Object3D>;
  restoreTopology: (root: THREE.Object3D) => void;
}): boolean {
  if (!input.sourceNode || input.sourceNode.nodeId === input.copiedNode.nodeId) {
    return false;
  }

  const sourceObject = input.objectByNodeId.get(input.sourceNode.nodeId);
  if (
    !sourceObject
    || sourceObject === input.copiedObject
    || hasRenderablePayload(sourceObject)
    || !hasRenderablePayload(input.copiedObject)
  ) {
    return false;
  }

  const clonedPayloadRoots = cloneRepresentativePayloadToSourceHost({
    representativeObject: input.copiedObject,
    sourceObject,
    sourceNode: input.sourceNode,
  });
  if (clonedPayloadRoots.length === 0) {
    return false;
  }

  moveMisplacedReferenceCopyPayloadBindings({
    sourceNode: input.sourceNode,
    copiedNode: input.copiedNode,
  });
  clonedPayloadRoots.forEach((clonedPayloadRoot) => {
    input.restoreTopology(clonedPayloadRoot);
  });
  return detachRenderablePayloadFromCopiedHost(input.copiedObject);
}
//------ 装配体引用复制 payload 回填 结束------

/**
 * 解析引用复制 materialize 的源对象。
 *
 * @param metadata 引用复制元数据。
 * @param objectByNodeId assembly node ID 到运行时对象的索引。
 * @param sourceIndex 复制族源对象索引。
 * @returns 源 Object3D；不存在时返回 null。
 */
function resolveReferenceCopyMaterializeSourceObject(
  metadata: AssemblyReferenceCopyMetadata,
  objectByNodeId: ReadonlyMap<string, THREE.Object3D>,
  sourceIndex: ReferenceCopyMaterializeSourceIndex,
): THREE.Object3D | null {
  const directSourceObject = objectByNodeId.get(metadata.sourceNodeId);
  if (directSourceObject && hasRenderablePayload(directSourceObject)) {
    return directSourceObject;
  }

  return (metadata.referenceFamilyId ? sourceIndex.objectByReferenceFamilyId.get(metadata.referenceFamilyId) : undefined)
    ?? (metadata.definitionId ? sourceIndex.objectByDefinitionId.get(metadata.definitionId) : undefined)
    ?? (metadata.sourcePartFileUuid ? sourceIndex.objectByDefinitionId.get(metadata.sourcePartFileUuid) : undefined)
    ?? null;
}
//------ 装配体引用复制族 结束------

/**
 * 解析旧离线包 display payload 中可能残留的“复制根到源根”偏移。
 *
 * 设计说明：
 * 旧导出链路可能把复制体根节点 transform 写入 assembly.json，同时又让 display 子节点
 * 保留 `copy^-1 * source` 的局部矩阵，导入后复制体会落回源位置。这里只在子树根矩阵
 * 明确等于该偏移时才修正，避免破坏已经规范化的包。
 *
 * @param input 解析输入。
 * @returns 需要左乘到 payload 根的校正矩阵；无需校正时返回 null。
 */
function resolveLegacyPayloadRebaseMatrix(input: {
  copiedNode: PuppyAssemblyNode;
  sourceNode: PuppyAssemblyNode | null;
  payloadRoot: THREE.Object3D;
}): THREE.Matrix4 | null {
  if (!input.sourceNode?.transform || !input.copiedNode.transform) {
    return null;
  }

  const sourceMatrix = createMatrixFromAssemblyNode(input.sourceNode);
  const copiedMatrix = createMatrixFromAssemblyNode(input.copiedNode);
  const legacyDelta = new THREE.Matrix4().copy(copiedMatrix).invert().multiply(sourceMatrix);
  input.payloadRoot.updateMatrix();
  if (!areMatricesClose(input.payloadRoot.matrix, legacyDelta)) {
    return null;
  }

  return legacyDelta.invert();
}

/**
 * 收集需要旧包矩阵校正的 payload 根对象。
 *
 * @param input 收集输入。
 * @returns payload 根对象与校正矩阵。
 */
function collectLegacyPayloadRebaseTargets(input: {
  copiedObject: THREE.Object3D;
  copiedNode: PuppyAssemblyNode;
  sourceNode: PuppyAssemblyNode | null;
}): Array<{ object: THREE.Object3D; rebaseMatrix: THREE.Matrix4 }> {
  const targets: Array<{ object: THREE.Object3D; rebaseMatrix: THREE.Matrix4 }> = [];
  input.copiedObject.traverse((object) => {
    if (object === input.copiedObject || targets.some((target) => isAncestorOf(target.object, object))) {
      return;
    }
    if (!hasRenderablePayload(object)) {
      return;
    }

    const rebaseMatrix = resolveLegacyPayloadRebaseMatrix({
      copiedNode: input.copiedNode,
      sourceNode: input.sourceNode,
      payloadRoot: object,
    });
    if (rebaseMatrix) {
      targets.push({
        object,
        rebaseMatrix,
      });
    }
  });
  return targets;
}

/**
 * 修正已有复制体 display payload 的旧包偏移。
 *
 * @param input 修正输入。
 */
function normalizeExistingReferenceCopyPayload(input: {
  copiedObject: THREE.Object3D;
  copiedNode: PuppyAssemblyNode;
  sourceNode: PuppyAssemblyNode | null;
}): void {
  const targets = collectLegacyPayloadRebaseTargets(input);
  targets.forEach((target) => {
    premultiplyLocalMatrix(target.object, target.rebaseMatrix);
  });
  if (targets.length > 0) {
    input.copiedObject.updateMatrixWorld(true);
  }
}

/**
 * 应用引用复制节点保存的局部矩阵。
 *
 * @param object 目标对象。
 * @param node 引用复制装配节点。
 */
function applyReferenceCopyTransform(
  object: THREE.Object3D,
  node: PuppyAssemblyNode,
): void {
  if (!node.transform) {
    throw new Error(`PuppyCAD 引用复制装配节点缺少本地矩阵: ${node.nodeId}`);
  }

  const matrix = new THREE.Matrix4().fromArray(node.transform);
  matrix.decompose(object.position, object.quaternion, object.scale);
  object.updateMatrix();
  object.updateWorldMatrix(true, true);
}

/**
 * 将 `.puppy` 中的引用复制节点 materialize 成运行时 Object3D。
 *
 * 设计说明：
 * 1. 保存阶段不会为复制体写入 display.glb mesh，因此导入阶段必须从源节点克隆运行时对象；
 * 2. 克隆使用共享 geometry/material，不复制大型几何 payload；
 * 3. 新对象只在运行时存在，assembly.json 仍然是“引用扩展 + 节点 transform”的轻量记录。
 *
 * @param input materialize 输入。
 * @returns materialize 统计。
 */
export function materializePuppyAssemblyReferenceCopies(input: {
  /** display.glb 根对象。 */
  readonly root: THREE.Object3D;
  /** assembly.json 文档。 */
  readonly assembly: PuppyAssemblyDocument;
  /** 可选拓扑源；提供时为复制体单独激活拓扑仓库。 */
  readonly topologySource?: ImportedTopologySource;
}): PuppyAssemblyReferenceCopyMaterializeResult {
  const objectByNodeId = collectObjectByAssemblyNodeId(input.root);
  const nodeById = createNodeById(input.assembly);
  const sourceIndex = createReferenceCopyMaterializeSourceIndex(input.assembly, objectByNodeId);
  const topologyRestorer = new PuppyReferenceCopyTopologyRestorer(input.assembly, input.topologySource);
  let materializedCount = 0;

  input.assembly.nodes.forEach((node) => {
    const referenceCopyMetadata = readReferenceCopyMetadata(node);
    const lineageMetadata = readReferenceLineageMetadata(node);
    const existingObject = objectByNodeId.get(node.nodeId);
    if (existingObject) {
      writeReferenceCopyRuntimeMetadata(existingObject, referenceCopyMetadata, lineageMetadata);
      if (!referenceCopyMetadata) {
        return;
      }

      const sourceNode = nodeById.get(referenceCopyMetadata.sourceNodeId) ?? null;
      normalizeExistingReferenceCopyPayload({
        copiedObject: existingObject,
        copiedNode: node,
        sourceNode,
      });
      repairMisplacedReferenceCopyRepresentativePayload({
        copiedObject: existingObject,
        copiedNode: node,
        sourceNode,
        objectByNodeId,
        restoreTopology: root => topologyRestorer.restore(root),
      });
      applyReferenceCopyTransform(existingObject, node);
      if (hasRenderablePayload(existingObject)) {
        topologyRestorer.restore(existingObject);
        return;
      }
    }

    if (!referenceCopyMetadata) {
      return;
    }

    const sourceObject = resolveReferenceCopyMaterializeSourceObject(
      referenceCopyMetadata,
      objectByNodeId,
      sourceIndex,
    );
    if (!sourceObject) {
      throw new Error(`PuppyCAD 引用复制缺少源运行时对象: ${referenceCopyMetadata.sourceNodeId}`);
    }

    const expansion = expandReferenceCopyDerivedNodes({
      assembly: input.assembly,
      copiedRootNode: node,
      referenceCopyMetadata,
      nodeById,
    });
    const clonedPayloadRoots = cloneReferenceCopyPayloadRoots({
      sourceObject,
      expansion,
    });
    let materializedObject: THREE.Object3D;
    if (existingObject) {
      attachReferenceCopyPayloadRootsToExistingHost({
        payloadRoots: clonedPayloadRoots,
        existingObject,
      });
      materializedObject = existingObject;
    } else {
      materializedObject = createReferenceCopyRuntimeHost({
        node,
        referenceCopyMetadata,
        lineageMetadata,
        payloadRoots: clonedPayloadRoots,
      });
      const parentObject = node.parentNodeId ? objectByNodeId.get(node.parentNodeId) : undefined;
      addObject3DToParent(materializedObject, parentObject ?? input.root);
    }
    indexMaterializedObjects(materializedObject, objectByNodeId);
    indexReferenceCopyMaterializeSourceNode(node, materializedObject, sourceIndex);
    topologyRestorer.restore(materializedObject);
    materializedCount += 1;
  });

  return { materializedCount };
}
